import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PriceDisplay from '../components/PriceDisplay';
import { Trash2, Plus, Minus, ShoppingBag, Tag, X } from 'lucide-react';
import { useCart } from '../context/CartContext';

const CartPage = () => {
  const { cartItems, updateQuantity, removeFromCart, getCartTotal, getCartCount,
          discount, applyDiscountCode, removeDiscount, getDiscountAmount, getFinalTotal } = useCart();
  const MAX_QUANTITY_PER_PRODUCT = 3;
  const MAX_CART_ITEMS = 10;
  
  const [codeInput, setCodeInput] = useState('');
  const [applying, setApplying] = useState(false);
  const [codeMsg, setCodeMsg] = useState(null); // {type: 'success'|'error', text}
  
  const handleApplyCode = async () => {
    if (!codeInput.trim()) return;
    setApplying(true);
    setCodeMsg(null);
    // Get email from localStorage if user logged in
    let email = '';
    try {
      const user = localStorage.getItem('user');
      if (user) email = JSON.parse(user).email || '';
    } catch {}
    
    const res = await applyDiscountCode(codeInput.trim(), email);
    setCodeMsg({ type: res.success ? 'success' : 'error', text: res.message });
    if (res.success) setCodeInput('');
    setApplying(false);
  };

  const handleUpdateQuantity = (item, newQuantity) => {
    // Per-product limit kontrolü
    if (newQuantity > MAX_QUANTITY_PER_PRODUCT) {
      alert(`Her üründen maksimum ${MAX_QUANTITY_PER_PRODUCT} adet sipariş verebilirsiniz!`);
      return;
    }
    
    // Stok kontrolü
    if (item.stock && newQuantity > item.stock) {
      alert(`Bu üründen maksimum ${item.stock} adet sepete ekleyebilirsiniz!`);
      return;
    }
    
    // Toplam sepet limiti kontrolü
    const currentTotal = cartItems.reduce((sum, i) => sum + i.quantity, 0);
    const difference = newQuantity - item.quantity;
    
    if (currentTotal + difference > MAX_CART_ITEMS) {
      alert(`Sepetinizde maksimum ${MAX_CART_ITEMS} ürün olabilir!`);
      return;
    }
    
    updateQuantity(item.id, newQuantity);
  };

  const handleRemoveItem = (id) => {
    removeFromCart(id);
  };

  const subtotal = getCartTotal();
  const shipping = 0; // Free shipping
  const discountAmount = getDiscountAmount();
  const total = getFinalTotal() + shipping;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header cartCount={getCartCount()} />

      <div className="container mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-8">Sepetim</h1>

        {cartItems.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <ShoppingBag size={64} className="mx-auto text-gray-300 mb-4" />
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Sepetiniz Boş</h2>
            <p className="text-gray-600 mb-6">Henüz ürün eklemediniz.</p>
            <Link
              to="/"
              className="inline-block bg-red-600 text-white px-8 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              Alışverişe Başla
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map((item) => (
                <div key={item.id} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex gap-6">
                    <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover border-2 border-gray-300 shadow-sm"
                      />
                    </div>

                    <div className="flex-1">
                      <h3 className="font-medium text-gray-800 mb-2">{item.name}</h3>
                      <div className="mb-4">
                        <PriceDisplay 
                          price={item.price}
                          oldPrice={item.old_price}
                          discount={item.discount}
                          size="medium"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center border border-gray-300 rounded-lg">
                            <button
                              onClick={() => handleUpdateQuantity(item, item.quantity - 1)}
                              className="p-2 hover:bg-gray-100 transition-colors disabled:opacity-50"
                              disabled={item.quantity <= 1}
                            >
                              <Minus size={16} />
                            </button>
                            <span className="px-4 py-2 font-semibold">{item.quantity}</span>
                            <button
                              onClick={() => handleUpdateQuantity(item, item.quantity + 1)}
                              className="p-2 hover:bg-gray-100 transition-colors disabled:opacity-50"
                              disabled={
                                item.quantity >= MAX_QUANTITY_PER_PRODUCT ||
                                (item.stock && item.quantity >= item.stock) ||
                                getCartCount() >= MAX_CART_ITEMS
                              }
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                          {item.quantity >= MAX_QUANTITY_PER_PRODUCT && (
                            <p className="text-xs text-orange-600">
                              ⚠️ Her üründen maksimum {MAX_QUANTITY_PER_PRODUCT} adet
                            </p>
                          )}
                          {item.stock && item.quantity >= item.stock && item.stock < MAX_QUANTITY_PER_PRODUCT && (
                            <p className="text-xs text-orange-600">
                              ⚠️ Stok limiti: {item.stock} adet
                            </p>
                          )}
                          {item.stock && item.stock < 10 && item.stock > item.quantity && (
                            <p className="text-xs text-orange-500">
                              Son {item.stock} adet!
                            </p>
                          )}
                          {getCartCount() >= MAX_CART_ITEMS && (
                            <p className="text-xs text-red-600">
                              🛒 Sepet limiti: {MAX_CART_ITEMS} ürün
                            </p>
                          )}
                        </div>

                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="text-red-500 hover:text-red-600 transition-colors flex items-center gap-2"
                        >
                          <Trash2 size={18} />
                          <span className="text-sm">Kaldır</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
                <h2 className="text-xl font-bold mb-6">Sipariş Özeti</h2>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ara Toplam</span>
                    <span className="font-semibold">{subtotal.toLocaleString('tr-TR')} TL</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Kargo</span>
                    <span className="font-semibold text-green-600">Ücretsiz</span>
                  </div>
                  
                  {/* Discount line */}
                  {discount && (
                    <div className="flex justify-between items-center bg-green-50 p-2 rounded">
                      <div className="flex items-center gap-2">
                        <Tag size={16} className="text-green-600" />
                        <span className="text-sm text-green-700" data-testid="applied-discount-code">
                          {discount.code} (%{discount.percent})
                        </span>
                        <button 
                          onClick={removeDiscount}
                          className="text-red-500 hover:text-red-700"
                          data-testid="remove-discount-btn"
                          aria-label="İndirimi kaldır"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <span className="font-semibold text-green-700">
                        -{discountAmount.toLocaleString('tr-TR')} TL
                      </span>
                    </div>
                  )}
                  
                  <div className="border-t pt-3 flex justify-between">
                    <span className="text-lg font-bold">Toplam</span>
                    <span className="text-lg font-bold text-red-600" data-testid="cart-final-total">
                      {total.toLocaleString('tr-TR')} TL
                    </span>
                  </div>
                </div>

                {/* Discount code input */}
                {!discount && (
                  <div className="mb-4 p-3 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                    <label className="flex items-center gap-1 text-xs font-semibold text-gray-700 mb-2">
                      <Tag size={14} /> İndirim Kodunuz Var mı?
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={codeInput}
                        onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyCode()}
                        placeholder="ÖRN: YENI10"
                        disabled={applying}
                        data-testid="discount-code-input"
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-600 uppercase"
                      />
                      <button
                        onClick={handleApplyCode}
                        disabled={applying || !codeInput.trim()}
                        data-testid="apply-discount-btn"
                        className="px-4 py-2 text-sm bg-gray-800 text-white rounded hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                      >
                        {applying ? '...' : 'Uygula'}
                      </button>
                    </div>
                    {codeMsg && (
                      <p 
                        data-testid="discount-code-message"
                        className={`mt-2 text-xs ${codeMsg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}
                      >
                        {codeMsg.text}
                      </p>
                    )}
                  </div>
                )}

                <Link
                  to="/checkout"
                  className="block w-full bg-red-600 text-white text-center py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold mb-3"
                >
                  Siparişi Tamamla
                </Link>

                <Link
                  to="/"
                  className="block w-full border-2 border-gray-300 text-gray-700 text-center py-3 rounded-lg hover:border-red-600 hover:text-red-600 transition-colors font-medium"
                >
                  Alışverişe Devam Et
                </Link>

                <div className="mt-6 p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-orange-800">
                    <strong>🚚 Ücretsiz Kargo</strong><br />
                    Türkiye'nin her yerine ücretsiz teslimat
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;
